import React, { useState, useEffect } from "react";
import { Plus } from "lucide-react";
import ExercisePlanCreatePopup from "../Exercise_Creation/ExercisePlanCreatePopup";
import { useToast, ToastContainer } from "../../Ui_Components/ToastMessage";
import { useAuth } from "../../../context/auth/AuthContext";
import { getAllExercises } from "../../../services/subPlans";
import PermissionGate from "../../Auth/PermissionGate";
import { Button } from "../../Ui_Components/Button";

interface ExercisePlanTabProps {
  userId: number;
  isDeactivated?: boolean;
}

interface ExercisePlan {
  id: number;
  plan_name: string;
  created_by: string;
  created_on: string;
  type: string;
  status: "Active" | "Approval Pending" | "Draft" | "In-Active";
  time?: string;
  duration?: string;
}

interface ExerciseApiItem {
  user_id: number;
  exercise_id: number;
  exercise_type: string;
  starting_time_hour: string;
  starting_time_minute: string;
  starting_time_period: string;
  duration_hours: string;
  duration_minutes: string;
  currentdatetime_exercise: string;
  exercise_date: string;
  exercise_status: string;
  nutrist_id: string;
}

const shimmerStyles = `
  @keyframes shimmer {
    0% { background-position: -1000px 0; }
    100% { background-position: 1000px 0; }
  }
  .shimmer {
    animation: shimmer 2s infinite;
    background: linear-gradient(
      to right,
      #f0f0f0 0%,
      #e0e0e0 20%,
      #f0f0f0 40%,
      #f0f0f0 100%
    );
    background-size: 1000px 100%;
  }
`;

const mapStatus = (status: string): ExercisePlan["status"] => {
  switch (status) {
    case "6": return "Approval Pending";
    case "2": return "Active";
    case "3": return "In-Active";
    default:  return "Draft";
  }
};

const mapApiItemToPlan = (
  item: ExerciseApiItem,
  loggedInNutristId: number,
  nutristName: string
): ExercisePlan => {
  const hour = item.starting_time_hour.padStart(2, "0");
  const minute = item.starting_time_minute.padStart(2, "0");
  const period = item.starting_time_period.toLowerCase();
  const time = `${hour}:${minute} ${period}`;

  const durationHours = parseInt(item.duration_hours, 10);
  const durationMinutes = parseInt(item.duration_minutes, 10);
  let duration = "";
  if (durationHours > 0 && durationMinutes > 0) {
    duration = `${durationHours} hr ${durationMinutes} min`;
  } else if (durationHours > 0) {
    duration = `${durationHours} hr`;
  } else {
    duration = `${durationMinutes} min`;
  }

  const isCurrentUser =
    Number(item.nutrist_id) === loggedInNutristId ||
    (!!nutristName &&
      !!item.nutrist_id &&
      item.nutrist_id.toLowerCase().startsWith(nutristName.toLowerCase()));

  return {
    id: item.exercise_id,
    plan_name: item.exercise_type,
    created_by: isCurrentUser ? "You" : item.nutrist_id,
    created_on: item.currentdatetime_exercise,
    type: item.exercise_type,
    status: mapStatus(item.exercise_status),
    time,
    duration,
  };
};

const ExercisePlanTab: React.FC<ExercisePlanTabProps> = ({ userId, isDeactivated = false }) => {
  const { user } = useAuth();
  const loggedInNutristId = user?.nutrist_id ?? 0;
  const nutristName = [user?.nutrist_fname, user?.nutrist_lname]
    .filter(Boolean)
    .join(" ");

  const [activeFilter, setActiveFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [exercisePlans, setExercisePlans] = useState<ExercisePlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isCreatePopupOpen, setIsCreatePopupOpen] = useState(false);

  const { toasts, removeToast, success, error, warning, info } = useToast();

  const fetchExercisePlans = async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const json = await getAllExercises(userId);

      if (json.success === "1" && Array.isArray(json.data)) {
        setExercisePlans(
          json.data
            .map((item: ExerciseApiItem) =>
              mapApiItemToPlan(item, loggedInNutristId, nutristName)
            )
            .sort((a: ExercisePlan, b: ExercisePlan) => b.id - a.id)
        );
      } else {
        setFetchError(json.message ?? "Failed to load exercise plans.");
        setExercisePlans([]);
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "An unexpected error occurred.";
      setFetchError(message);
      setExercisePlans([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExercisePlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, loggedInNutristId]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Active":           return "bg-[#EBF7F4] text-[#0DB5B5]";
      case "Approval Pending": return "bg-[#FFF3DE] text-[#FF9500]";
      case "Draft":            return "bg-[#E9E9E9] text-[#202020]";
      case "In-Active":        return "bg-[#FFDADA] text-[#FF3B30]";
      default:                 return "bg-gray-100 text-gray-600";
    }
  };

  const filteredPlans = exercisePlans.filter((plan) => {
    const matchesFilter =
      activeFilter === "all" ||
      (activeFilter === "inactive" && plan.status === "In-Active") ||
      plan.status.toLowerCase().replace(" ", "").replace("-", "") === activeFilter;
    const matchesSearch = plan.plan_name
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const handleCreateClick = () => setIsCreatePopupOpen(true);
  const handlePopupClose = () => setIsCreatePopupOpen(false);
  const handlePlanSubmit = () => fetchExercisePlans();

  return (
    <>
      <style>{shimmerStyles}</style>
      <ToastContainer toasts={toasts} onClose={removeToast} />

      <div className="h-full flex flex-col bg-white rounded-md border border-[#F3F4F6] overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold font-inter text-[#1F1F1F]">
              Recommended exercise
            </h2>
            <PermissionGate resource="exercise_plan" action="create">
              <Button
                variant="primary"
                onClick={() => !isDeactivated && handleCreateClick()}
                disabled={isDeactivated}
                startIcon={<Plus className="w-4 h-4" />}
              >
                Create
              </Button>
            </PermissionGate>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Fixed Table Header */}
          <div className="flex-shrink-0 overflow-x-auto bg-white border-b border-[#111C2D33] custom-scrollbar-overlay">
            <table className="w-full min-w-[900px] table-fixed">
              <thead>
                <tr className="bg-white">
                  <th className="text-left pt-2 pb-3 px-6 text-[#646464] text-sm 3xl:text-base font-medium font-inter tracking-wider w-[3%]">
                    Sl
                  </th>
                  <th className="text-left pt-2 pb-3 px-6 text-[#646464] text-sm 3xl:text-base font-medium font-inter tracking-wider w-[23%]">
                    Exercise name
                  </th>
                  <th className="text-left pt-2 pb-3 px-6 text-[#646464] text-sm 3xl:text-base font-medium font-inter tracking-wider w-[14%]">
                    Time
                  </th>
                  <th className="text-left pt-2 pb-3 px-6 text-[#646464] text-sm 3xl:text-base font-medium font-inter tracking-wider w-[14%]">
                    Duration
                  </th>
                  <th className="text-left pt-2 pb-3 px-6 text-[#646464] text-sm 3xl:text-base font-medium font-inter tracking-wider w-[18%]">
                    Created By
                  </th>
                  <th className="text-left pt-2 pb-3 px-6 text-[#646464] text-sm 3xl:text-base font-medium font-inter tracking-wider w-[15%]">
                    Created On
                  </th>
                </tr>
              </thead>
            </table>
          </div>

          {/* Scrollable Body */}
          <div className="flex-1 overflow-x-auto overflow-y-auto custom-scrollbar-overlay">
            {loading ? (
              <div className="h-full shimmer" />
            
            ) : (
              <>
                <table className="w-full min-w-[900px] table-fixed">
                  <tbody className="bg-white divide-y divide-[#F3F4F6]">
                    {filteredPlans.map((plan, index) => (
                      <tr
                        key={plan.id}
                        className="hover:bg-[#F9FEFE] transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F] w-[3%]">
                          {String(index + 1).padStart(2, "0")}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap w-[23%]">
                          <div className="flex items-center gap-2">
                            <span className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
                              {plan.plan_name}
                            </span>
                            <span
                              className={`px-1.5 py-0.5 rounded-[24px] text-[8px] 3xl:text-[10px] font-semibold font-inter flex-shrink-0 ${getStatusColor(plan.status)}`}
                            >
                              {plan.status}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F] w-[14%]">
                          {plan.time || "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F] w-[14%]">
                          {plan.duration || "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F] w-[18%]">
                          {plan.created_by}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F] w-[15%]">
                          {plan.created_on}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {filteredPlans.length === 0 && (
                  <div className="px-6 py-12 text-center">
                    <p className="text-sm text-gray-500">No exercise plans found</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {!isDeactivated && (
        <ExercisePlanCreatePopup
          isOpen={isCreatePopupOpen}
          onClose={handlePopupClose}
          onSubmit={handlePlanSubmit}
          userId={userId}
          onShowToast={(type, title, message, duration) => {
            switch (type) {
              case "success": success(title, message, duration); break;
              case "error":   error(title, message, duration);   break;
              case "warning": warning(title, message, duration); break;
              case "info":    info(title, message, duration);    break;
            }
          }}
        />
      )}
    </>
  );
};

export default ExercisePlanTab;